import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { clearAuthCache } from "@/services/authService";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 5秒保底：自動取消 loading
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("請輸入郵箱");
      return;
    }
    if (password.length < 6) {
      setError("密碼至少6位");
      return;
    }

    // ✅ 登入前強制清除所有舊緩存
    clearAuthCache();
    try {
      localStorage.clear();
    } catch {
      // ignore
    }

    setIsLoading(true);

    // ⏱ 5秒保底機制：若還沒跳轉，自動取消 loading 並提示
    timeoutRef.current = setTimeout(() => {
      setIsLoading(false);
      setError("登入逾時，請檢查網路或重新整理頁面後重試");
    }, 5000);

    try {
      const { data, error: signInErr } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (signInErr) {
        throw signInErr;
      }
      if (!data.user) {
        throw new Error("登入成功但無法取得用戶資訊");
      }

      // ✅ 登入成功 —— 立即停止保底計時器
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // ✅ 強制權限判定：admin 信箱無視資料庫欄位，直接視為 Admin
      const isForcedAdmin = data.user.email === "admin@jishi-trade.com";

      // ✅ 立即跳轉，完全不等待任何資料庫讀取
      const target = isForcedAdmin ? "/admin/dashboard" : "/dashboard";
      window.location.assign(target);
    } catch (err: unknown) {
      // 取消保底計時器
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      let msg = "登入失敗，請檢查帳號密碼";
      if (err instanceof Error) {
        msg = err.message;
      } else if (typeof err === "object" && err !== null && "message" in err) {
        msg = String((err as Record<string, unknown>).message);
      } else if (typeof err === "string") {
        msg = err;
      }

      if (!msg || msg === "undefined" || msg === "null") {
        msg = "登入失敗，請檢查帳號密碼";
      }

      const lower = msg.toLowerCase();
      if (lower.includes("invalid login credentials")) {
        msg = "帳號或密碼錯誤，請重新輸入";
      } else if (lower.includes("email not confirmed")) {
        msg = "此信箱尚未驗證，請聯繫管理員手動開通";
      } else if (lower.includes("user not found") || lower.includes("invalid credentials")) {
        msg = "找不到此帳號，請先註冊";
      } else if (lower.includes("too many requests")) {
        msg = "嘗試次數過多，請稍後再試";
      } else if (lower.includes("network")) {
        msg = "網路連線異常，請檢查網路後重試";
      } else if (lower.includes("row-level security") || lower.includes("42501")) {
        msg = "資料庫權限錯誤 (RLS)，請聯繫管理員";
      }

      setError(msg);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="pt-20 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-3">
              <img
                src="https://static.readdy.ai/image/823a8f074b021e82ba2884013917a5c5/a23db1fedd75cee41aae25a9154ab297.jpeg"
                alt="吉時國際貿易"
                className="h-10 w-auto object-contain rounded"
              />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">分銷商登入</h1>
            <p className="text-sm text-gray-500">登入後開始挑選商品、下單發貨</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm text-gray-600 mb-1.5 block">郵箱地址</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                disabled={isLoading}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400 disabled:opacity-50"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1.5 block">密碼</label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="輸入密碼"
                  disabled={isLoading}
                  className="w-full px-3 py-2.5 pr-10 rounded-lg border border-gray-200 text-sm outline-none focus:border-amber-400 disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  disabled={isLoading}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 cursor-pointer disabled:opacity-50"
                >
                  <i className={`ri-${showPwd ? "eye-line" : "eye-off-line"} text-sm`}></i>
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-600">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 rounded-xl bg-amber-500 text-white font-medium text-sm hover:bg-amber-600 transition-all active:scale-[0.98] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isLoading ? "登入中…" : "登入"}
            </button>
          </form>

          <div className="mt-6 flex items-center justify-between text-sm">
            <Link
              to="/register"
              className="text-amber-600 font-medium hover:underline cursor-pointer"
            >
              立即註冊
            </Link>
            <button
              type="button"
              onClick={() => setError("請聯繫管理員重置密碼")}
              className="text-gray-500 hover:text-gray-700 cursor-pointer"
            >
              忘記密碼？
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}